import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Replay as ReplayIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

import { DataGridPro } from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { formatDate, formatDateOnly, formatDateTime, initializeFormats, getFormatConfig } from '../../../utils/global';

const TreasuryPaymentDrawer = ({ open, onClose, selectedFile, onFileClick }) => {
  const [drawerFiles, setDrawerFiles] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dateTimeFormat, setDateTimeFormat] = useState('DD/MM/YYYY hh:mm:ss a');
  
  // File details state
  const [fileDetails, setFileDetails] = useState(null);
  const [extractedData, setExtractedData] = useState([]);
  
  // JSON view state
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonData, setJsonData] = useState(null);

  const { showSuccess, showError, showInfo } = useToast();
  const { canEdit } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);

  // Initialize formats when component mounts
  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      initializeFormats(tenantId).then(() => {
        const config = getFormatConfig();
        if (config.DateTimeFormat) {
          setDateTimeFormat(config.DateTimeFormat);
        }
      });
    }
  }, []);

  // Load file details when selectedFile changes
  useEffect(() => {
    if (selectedFile && open) {
      loadFileDetails();
    }
  }, [selectedFile, open]);

  const loadFileDetails = async () => {
    if (!selectedFile?.id) return;

    try {
      setDrawerLoading(true);
      setDrawerError(null);

      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      // Fetch file details with audit logs
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${selectedFile.id}/with-audit-logs?tenantId=${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch file details: ${response.status}`);
      }

      const data = await response.json();
      setFileDetails(data.document);

      // Load extracted data if available
      if (selectedFile.unitOfWorkId && selectedFile.fastApiFileInstanceId) {
        // Use the extracted output data from selectedFile if available
        if (selectedFile.extractedOutput) {
          setExtractedData(Array.isArray(selectedFile.extractedOutput) ? selectedFile.extractedOutput : []);
        }
      }

      showSuccess('File details loaded successfully');
    } catch (err) {
      console.error('Error loading file details:', err);
      setDrawerError(err.message || 'Failed to load file details');
      showError('Failed to load file details');
    } finally {
      setDrawerLoading(false);
    }
  };

  const loadExtractedData = async (file) => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      // Construct the S3 path for the extracted JSON file
      const s3Path = `t-${tenantId}/uow-${file.unitOfWorkId}/extracted/output/FI-${file.fastApiFileInstanceId}-WI-${file.processId}.json`;

      // Get presigned URL
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/presigned-url-for-path` +
        `?s3Path=${encodeURIComponent(s3Path)}` +
        `&tenantId=${encodeURIComponent(tenantId)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const { url } = await response.json();
        
        // Fetch the JSON data
        const jsonResponse = await fetch(url, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (jsonResponse.ok) {
          const data = await jsonResponse.json();
          setExtractedData(Array.isArray(data) ? data : [data]);
        }
      }
    } catch (error) {
      console.error('Error loading extracted data:', error);
      // Don't show error for extracted data as it might not be available yet
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRefresh = () => {
    loadFileDetails();
  };

  const handleViewJson = async (data) => {
    try {
      setJsonLoading(true);
      setJsonData(JSON.stringify(data, null, 2));
      setJsonDialogOpen(true);
    } catch (error) {
      showError('Failed to format JSON data');
    } finally {
      setJsonLoading(false);
    }
  };

  const handleRedrive = async () => {
    if (!fileDetails?.unitOfWorkId) {
      showError('Unit of Work ID not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get workflow execution using unitOfWorkId
      const workflowExecutionUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/uow/${fileDetails.unitOfWorkId}`;
      const workflowExecutionResponse = await fetch(workflowExecutionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!workflowExecutionResponse.ok) {
        throw new Error('Failed to get workflow execution');
      }

      const workflowData = await workflowExecutionResponse.json();
      const workflowExecutionId = workflowData.id;

      if (!workflowExecutionId) {
        throw new Error('Workflow execution ID not found');
      }

      // Call redrive API
      const redriveUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/${workflowExecutionId}/redrive`;
      const stepFunctionName = process.env.REACT_APP_ENV === 'qa' ? 'Conductor-v2-dev-workflow' : `Conductor-v2-${process.env.REACT_APP_ENV}-workflow`;
      const stateMachineArn = `arn:aws:states:eu-west-1:${process.env.REACT_APP_AWS_ACCOUNT_ID}:stateMachine:${stepFunctionName}`;

      const redriveResponse = await fetch(redriveUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stateMachineArn: stateMachineArn
        })
      });

      if (!redriveResponse.ok) {
        throw new Error('Failed to redrive workflow');
      }

      showSuccess('Workflow redrive initiated successfully');
      handleRefresh();
    } catch (error) {
      console.error('Error during redrive:', error);
      showError(`Failed to redrive workflow: ${error.message}`);
    }
  };

  const extractedDataColumns = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      minWidth: 80,
    },
    {
      field: 'invoiceNumber',
      headerName: 'Invoice Number',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'vendorName',
      headerName: 'Vendor Name',
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.amount ? `$${params.row.amount.toFixed(2)}` : 'N/A',
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.dueDate || 'N/A',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip 
          label={params.row.status || 'Unknown'} 
          size="small" 
          color={params.row.status === 'processed' ? 'success' : 'default'}
        />
      ),
    },
  ];

  const auditLogsColumns = [
    {
      field: 'createdAt',
      headerName: 'Timestamp',
      flex: 1.5,
      minWidth: 160,
      valueFormatter: (params) => params.value ? formatDateTime(params.value, dateTimeFormat) : 'N/A',
    },
    {
      field: 'service',
      headerName: 'Service',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value || 'N/A'} 
          size="small" 
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value || 'N/A'} 
          size="small" 
          color={params.value === 'success' ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'details',
      headerName: 'Details',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value ? params.value.toString() : 'N/A'}>
          <Typography noWrap sx={{ maxWidth: 200 }}>
            {params.value ? params.value.toString() : 'N/A'}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '90%',
            maxWidth: '1200px',
            height: '100vh',
          },
        }}
      >
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'white',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedFile?.name || 'File Details'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={handleRefresh} disabled={drawerLoading}>
                <RefreshIcon />
              </IconButton>
              {canEdit && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRedrive}
                  startIcon={<ReplayIcon />}
                  disabled={drawerLoading}
                >
                  Redrive
                </Button>
              )}
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Loading State */}
          {drawerLoading && (
            <LinearProgress sx={{ height: 2 }} />
          )}

          {/* Error State */}
          {drawerError && (
            <Alert severity="error" sx={{ m: 2 }}>
              {drawerError}
            </Alert>
          )}

          {/* File Info Card */}
          {fileDetails && (
            <Card sx={{ m: 2, bgcolor: 'white' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  File Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary">File Name</Typography>
                    <Typography variant="body2" noWrap>{fileDetails.name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary">Uploaded By</Typography>
                    <Typography variant="body2">{fileDetails.username || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary">Process Step</Typography>
                    <Chip 
                      label={fileDetails.processStep || 'N/A'} 
                      size="small" 
                      color={fileDetails.processStep === 'completed' ? 'success' : 'default'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary">Created At</Typography>
                    <Typography variant="body2">
                      {fileDetails.createdAt ? formatDateTime(fileDetails.createdAt, dateTimeFormat) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary">Unit of Work</Typography>
                    <Typography variant="body2" noWrap>{fileDetails.unitOfWorkId || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary">Dataset Type</Typography>
                    <Typography variant="body2">{selectedFile?.datasetType || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Box sx={{ px: 2, pt: 1 }}>
            <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable">
              <Tab label="Extracted Data" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ flex: 1, p: 2, overflow: 'hidden' }}>
            {activeTab === 0 && (
              <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Extracted Data
                  </Typography>
                  {extractedData.length > 0 && (
                    <Button
                      size="small"
                      onClick={() => handleViewJson(extractedData)}
                      startIcon={<VisibilityIcon />}
                    >
                      View JSON
                    </Button>
                  )}
                </Box>
                <Box sx={{ flex: 1, p: 2 }}>
                  {extractedData.length > 0 ? (
                    <DataGridPro
                      rows={extractedData}
                      columns={extractedDataColumns}
                      columnResizeMode="onEnd"
                      resizable
                      pagination
                      rowsPerPageOptions={[10, 25, 50]}
                      pageSize={25}
                      sx={{
                        '& .MuiDataGrid-root': {
                          border: 'none',
                        },
                      }}
                      disableRowSelectionOnClick
                    />
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: 200,
                      color: 'text.secondary'
                    }}>
                      <Typography>
                        {drawerLoading ? 'Loading extracted data...' : 'No extracted data available'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* JSON View Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>JSON Data</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            bgcolor: '#f5f5f5', 
            p: 2, 
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            maxHeight: '400px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap'
          }}>
            {jsonLoading ? (
              <CircularProgress />
            ) : (
              jsonData || 'No JSON data available'
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TreasuryPaymentDrawer;
